import { ItReportTemplateResponse, getReportTemplateCategories, ItReportCategoryResponse } from "../../services/reportService";
import { getAllLanguages } from "../../services/languageService";
import { Text, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Box, TextField, Inline, IconTick, Stack, Dropdown, IconDownload, Button } from "braid-design-system"
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LanguageResponse } from "../../services/languageService";
import ReportDrawer from './ReportDrawer';
import { getAllReportTemplates } from '../../services/reportService';

interface ReportsViewTableProps {
    reportList?: Array<ItReportTemplateResponse> | null;
    setLoadingMessage?: (message: string | null) => void;
}

const ReportsViewTable: React.FC<ReportsViewTableProps> = ({
    reportList,
}) => {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [categories, setCategories] = useState<ItReportCategoryResponse[]>([]);
    const [languageStatusMap, setLanguageStatusMap] = useState<{ [reportId: number]: number[] }>({});
    const [languages, setLanguages] = useState<LanguageResponse[]>([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [reportsData, setReportsData] = useState<Array<ItReportTemplateResponse> | null>(reportList || null);

    // Handle opening report in new tab using React Router navigate
    const handleViewReport = (reportId: number) => {
        // Use React Router navigate with state
        const reportUrl = `/reports/view/${reportId}`;
        // Open in new tab
        const newTab = document.createElement('a');
        newTab.href = reportUrl;
        newTab.target = '_blank';
        document.body.appendChild(newTab);
        newTab.click();
        document.body.removeChild(newTab);
    };

    // Sync prop -> local state
    React.useEffect(() => {
        setReportsData(reportList || null);
    }, [reportList]);

    // Load categories on mount
    React.useEffect(() => {
        const loadCategories = async () => {
            try {
                const categoriesResponse = await getReportTemplateCategories();
                setCategories(categoriesResponse);
            } catch (error) {
                // Silently handle error
            }
        };

        loadCategories();
    }, []);

    // Add "All Categories" option to the categories array for dropdown display
    const categoryOptions = [
        { id: 0, name: t('page.reports.view.all_categories', 'All Categories') },
        ...categories
    ];

    // Filter reports based on search term and category
    const filteredReports = useMemo(() => {
        const source = reportsData || reportList;
        if (!source) return source;

        const filtered = source.filter((report) => {
            const matchesSearch = !searchTerm.trim() || (
                report.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            const categoryName = report.category_name || 'Uncategorized';
            const matchesCategory = !selectedCategory || categoryName === selectedCategory;

            return matchesSearch && matchesCategory;
        });

        // Sort by name
        return filtered.sort((a, b) => {
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [reportList, reportsData, searchTerm, selectedCategory]);

    // Load language status for all reports and available languages
    React.useEffect(() => {
        const loadLanguageStatus = async () => {
            if (!filteredReports) return;

            const statusMap: { [reportId: number]: number[] } = {};
            const allLanguages = new Map<number, LanguageResponse>();

            for (const report of filteredReports) {
                if (report.id) {
                    // Use languages from report response instead of fetching separately
                    const uploadedLangIds = report.languages?.map((lang: LanguageResponse) => lang.id) || report.uploaded_languages || [];
                    statusMap[report.id] = uploadedLangIds;

                    // Collect all languages from all reports
                    if (report.languages && Array.isArray(report.languages)) {
                        report.languages.forEach((lang: LanguageResponse) => {
                            allLanguages.set(lang.id, {
                                id: lang.id,
                                language: lang.language,
                                display_name: lang.display_name,
                                code: lang.code,
                                is_active: true  // Set default value
                            });
                        });
                    }
                }
            }

            setLanguageStatusMap(statusMap);

            // If no languages from reports, try loading all available languages
            if (allLanguages.size === 0) {
                try {
                    const allLanguagesResponse = await getAllLanguages();
                    setLanguages(Array.isArray(allLanguagesResponse.languages) ? allLanguagesResponse.languages : []);
                } catch (error) {
                    // Silently handle error, languages won't be shown
                }
            } else {
                setLanguages(Array.from(allLanguages.values()));
            }
        };

        loadLanguageStatus();
    }, [filteredReports]);

    // Handler when a report is created/saved in the drawer
    const handleReportSaved = async () => {
        try {
            // Refresh list from API to get latest data
            const list = await getAllReportTemplates();
            setReportsData(list || null);
            setIsDrawerOpen(false);
        } catch (error) {
            // Even if refresh fails, just close drawer and rely on parent refresh
            setIsDrawerOpen(false);
        }
    };

    return (
        <Box paddingX="xsmall" paddingY={"small"}>

            <Box display={"flex"} justifyContent="flexEnd">
                <Inline space="small">
                    <Button tone="brandAccent" onClick={() => setIsDrawerOpen(true)}>
                        {t('page.reports.view.button.create', 'Create Report')}
                    </Button>
                    <TextField
                        aria-label={t('page.reports.view.search_aria', 'Search Reports')}
                        placeholder={t('page.reports.view.search_placeholder', 'Search by report name...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    />
                    <Dropdown
                        aria-label={t('page.reports.view.filter_category_aria', 'Filter by Category')}
                        value={selectedCategory}
                        placeholder={t('page.reports.view.filter_category_placeholder', 'Filter by category')}
                        onChange={(e) => setSelectedCategory((e.target as HTMLSelectElement).value)}
                    >
                        {categoryOptions.map((category) => (
                            <option key={category.id} value={category.id === 0 ? '' : category.name}>{category.name}</option>
                        ))}
                    </Dropdown>
                </Inline>
            </Box>

            <Box paddingY="large">
                <Table label={t('page.reports.view.table_label', 'Reports Table')}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell width="30%"><Text>{t('page.reports.view.header.name', 'Report Name')}</Text></TableHeaderCell>
                            <TableHeaderCell><Text>{t('page.reports.view.header.category', 'Category')}</Text></TableHeaderCell>
                            <TableHeaderCell><Text>{t('page.reports.view.header.languages', 'Languages')}</Text></TableHeaderCell>
                            <TableHeaderCell><Text align={"right"}>{t('page.reports.view.header.actions', 'Actions')}</Text></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {
                            filteredReports && filteredReports.length === 0 && (
                                <TableRow>
                                    <TableCell colspan={4}>
                                        <Text tone="secondary">{t('page.reports.view.empty', 'No reports found.')}</Text>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        {filteredReports?.map((report) => (
                            <TableRow key={report.id}>
                                <TableCell>
                                    <Text weight="strong">{report.name}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{report.category_name || 'Uncategorized'}</Text>
                                </TableCell>
                                <TableCell>
                                    <Stack space="small">
                                        {report.id && languageStatusMap[report.id] ? (
                                            languageStatusMap[report.id].length > 0 ? (
                                                <Stack space="xsmall">
                                                    {languageStatusMap[report.id].sort((a, b) => a - b).map((langId) => {
                                                        const language = Array.isArray(languages)
                                                            ? languages.find((l) => l.id === langId)
                                                            : undefined;
                                                        return (
                                                            <Inline key={langId} space="small" alignY="center">
                                                                <Text size="xsmall">{language?.display_name || `Unknown (${langId})`}</Text>
                                                                <IconTick size="xsmall" tone="positive" />
                                                            </Inline>
                                                        );
                                                    })}
                                                </Stack>
                                            ) : (
                                                <Text size="small" tone="secondary">{t('page.reports.view.no_languages', 'No languages')}</Text>
                                            )
                                        ) : (
                                            <Text size="small" tone="secondary">{t('page.reports.view.loading_languages', 'Loading...')}</Text>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">
                                    <Box
                                        cursor="pointer"
                                        onClick={() => {
                                            if (report.id) {
                                                handleViewReport(report.id);
                                            }
                                        }}>
                                        <Text
                                            tone="info"
                                            icon={<IconDownload />}
                                        >
                                            {t('page.reports.view.action.view_report', 'View Report')}
                                        </Text>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            {/* Report Drawer for create/edit */}
            <ReportDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onSaved={handleReportSaved}
                initialData={null}
            />
        </Box>
    );
}

export default ReportsViewTable;
